import type {
  DuplicateAction,
  ImportExecuteResult,
  ImportValidationResult,
} from '../../types/infrastructure'
import { executeImport, validateImportFile } from '../../services/endpoints'
import { buildExecuteRequest, preflightExecuteError } from './build-execute-request'

export type ImportPhase = 'idle' | 'validating' | 'validated' | 'executing' | 'complete' | 'error'

interface ValidateOptions {
  caseId: string | null
  file: File
  setPhase: (phase: ImportPhase) => void
  setLoading: (busy: boolean) => void
  setError: (message: string | null) => void
  setValidationResult: (result: ImportValidationResult | null) => void
  setImportResult: (result: ImportExecuteResult | null) => void
}

export async function runValidate(opts: ValidateOptions): Promise<ImportValidationResult | null> {
  if (!opts.caseId) {
    opts.setError('No case selected')
    return null
  }
  opts.setPhase('validating')
  opts.setLoading(true)
  opts.setError(null)
  opts.setValidationResult(null)
  opts.setImportResult(null)
  try {
    const result = await validateImportFile(opts.caseId, opts.file)
    opts.setValidationResult(result)
    opts.setPhase('validated')
    return result
  } catch (err) {
    opts.setError(err instanceof Error ? err.message : 'Failed to validate file')
    opts.setPhase('error')
    return null
  } finally {
    opts.setLoading(false)
  }
}

interface ExecuteOptions {
  caseId: string | null
  duplicateAction: DuplicateAction
  validationResult: ImportValidationResult | null
  setPhase: (phase: ImportPhase) => void
  setLoading: (busy: boolean) => void
  setError: (message: string | null) => void
  setImportResult: (result: ImportExecuteResult | null) => void
}

export async function runExecute(opts: ExecuteOptions): Promise<ImportExecuteResult | null> {
  const preflight = preflightExecuteError(opts.caseId, opts.validationResult, opts.duplicateAction)
  if (preflight) {
    opts.setError(preflight)
    return null
  }
  opts.setPhase('executing')
  opts.setLoading(true)
  opts.setError(null)
  try {
    const request = buildExecuteRequest(
      opts.validationResult as ImportValidationResult,
      opts.duplicateAction
    )
    const result = await executeImport(opts.caseId as string, request)
    opts.setImportResult(result)
    opts.setPhase('complete')
    return result
  } catch (err) {
    opts.setError(err instanceof Error ? err.message : 'Failed to execute import')
    opts.setPhase('error')
    return null
  } finally {
    opts.setLoading(false)
  }
}
